import React, { useMemo, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { SearchField, hooks as coreHooks } from '@adc/ui-core'
import { search, primitives } from '@adc/blueprint'
import classNames from 'classnames/bind'
import { Suggestion, CategoryLayout, Category } from './suggestion'
import {
  PLACEHOLDER_TEXT,
  MAX_AMOUNT_OF_ADDRESS_RESULTS,
  MAX_AMOUNT_RECENT_SEARCHES,
} from './constants'
import cls from './styles.scss'
import HighlightedMatches from './suggestion/HighlightedMatches'
import { useApplicationContext } from '../auctionApplication/context'

const cx = classNames.bind(cls)

const SPECIAL_SUGGESTION_TYPES_INDEXES = {
  [search.constants?.GenericLocations?.CURRENT_LOCATION || 'CURRENT_LOCATION']:
    'current_location',
  [search.constants?.GenericLocations?.ALL_LOCATIONS || 'ALL_LOCATIONS']:
    'all_locations',
}
function Search({
  elmId = 'main',
  filters,
  onSearchSubmit,
  user,
  categorizeResults = false,
  placeholder = PLACEHOLDER_TEXT,
  clearInputOnSearch = false,
  includedSuggestionTypes,
  variant,
  recognizedLocations,
}) {
  const currentLocation = coreHooks.useCurrentLocation()

  const deviceStorageRef = useRef()

  coreHooks.useMountEffect(() => {
    deviceStorageRef.current = new primitives.DeviceStorageAdapter({
      storage: new primitives.WebLocalStorage({ namespace: 'recentSearch' }),
    })
  })

  const { graphWebsocket } = useApplicationContext()

  const { data, actions } = search.hooks.useLocationSuggestions({
    user,
    includedSuggestionTypes,
    filters,
    currentLocation,
    recognizedLocations,
    deviceStorage: deviceStorageRef.current,
    elmId,
    maxRecentSearches: MAX_AMOUNT_RECENT_SEARCHES,
    graphWebsocket,
  })

  const onSearchChange = useCallback(
    (_, searchInfo) => {
      actions.update({ searchText: searchInfo.value })
    },
    [actions]
  )

  /**
   * This is kind of a web-only edge case where the user can use the keyboard to select a
   * suggestion, and potentially close the dropdown and then hit the search icon.  We use
   * this to know what suggestion the user was selecting.
   */
  const onSelectionChange = useCallback(
    (_, resultData) => {
      actions.trackSelectionChange(data.suggestions[resultData.result.index])
    },
    [actions, data.suggestions]
  )

  const invokeSearch = useCallback(
    suggestion => {
      // TODO:
      // actions obj doesn't have updateRecentSearches function and trackSelectionChange
      // in unit tests for some reason
      actions?.updateRecentSearches?.(suggestion)
      if (onSearchSubmit) {
        onSearchSubmit(suggestion)
        if (clearInputOnSearch) actions.update({ searchText: '' })
      } else {
        window.location.assign(suggestion.link)
      }
    },
    [onSearchSubmit, clearInputOnSearch, actions]
  )

  /**
   * Semantic gives us a synthetic event and a data object, where the data object is all the props
   */
  const onResultSelect = useCallback(
    (e, resultData) => invokeSearch(data.suggestions[resultData.result.index]),
    [data.suggestions, invokeSearch]
  )

  /**
   * In this case, user just clicked on the search icon, so we will auto select suggestion for them
   */
  const onSearchIconClick = useCallback(() => {
    invokeSearch(actions.autoSelectSuggestion())
  }, [actions, invokeSearch])

  const onFocus = useCallback(
    e => {
      if (e.target.value === 'All Locations') {
        actions.update({ searchText: '' })
      }
    },
    [actions]
  )

  /**
   * Formats the results for semantic SearchField...
   * Note: we used to tack on the whole suggestion data object and then later in onResultSelect use that, however
   * doing so causes the SearchField to attempt to tack a attribute called `data` into the rendered DOM row.
   * So, instead, we don't do that as Semantic will provide us the index of the result, and we can use that to look up
   * the suggestion data in our own state.
   */
  const formatResult = useCallback(
    (suggestion, index = false) => ({
      key: `${index}_${suggestion.type}`,
      className: `${suggestion.type.replace(/_/, '-')}-autosuggest-row`,
      title: suggestion.label,
      index,
      renderer: () => (
        <Suggestion
          elmId='' // QA has requested we don't set this (prefix) for now to jive with existing tests
          index={
            Object.keys(SPECIAL_SUGGESTION_TYPES_INDEXES).includes(
              suggestion.label
            )
              ? SPECIAL_SUGGESTION_TYPES_INDEXES[suggestion.label]
              : index
          }
          label={
            <HighlightedMatches
              suggestion={suggestion}
              searchText={data.searchText}
            />
          }
          {..._.pick(suggestion, [
            'icon',
            'subLabel',
            'hideSubLabelWhenCategorized',
          ])}
          categorizeResults={categorizeResults}
        />
      ),
    }),
    [categorizeResults, data.searchText]
  )

  const results = useMemo(() => {
    if (!categorizeResults) {
      return data.suggestions === null ? [] : data.suggestions.map(formatResult)
    }
    /**
     * This will format the data in the way Semantic-ui-react expects it for the category layout, which is an object
     * with keys of the category name, and an array of formatted results under that category.
     */
    if (data.suggestions && search.utils.getFormattedResultsWithCategories) {
      return search.utils.getFormattedResultsWithCategories(
        data.suggestions,
        formatResult
      )
    }
    return {}
  }, [data.suggestions, formatResult, categorizeResults])

  return (
    <div className={cx('search-container', { [variant]: variant })}>
      <div
        className={cx('search-form', 'new-header')}
        data-elm-id={`${elmId}_search`}
      >
        <SearchField
          name='Search'
          label='Search'
          placeholder={placeholder}
          elmId={`${elmId}_search_input`}
          className={cx('search-field')}
          selectFirstResult
          loading={data.loading}
          minCharacters={0}
          results={results}
          onResultSelect={onResultSelect}
          onSearchChange={onSearchChange}
          onSearchIconClick={onSearchIconClick}
          onSelectionChange={onSelectionChange}
          stickyOpen={false}
          value={data.searchText}
          hideWrapper
          onFocus={onFocus}
          category={categorizeResults}
          categoryLayoutRenderer={CategoryLayout}
          categoryRenderer={Category}
          deleteIconEnabled
        />
      </div>
    </div>
  )
}

Search.propTypes = {
  elmId: PropTypes.string,
  filters: PropTypes.object,
  onSearchSubmit: PropTypes.func,
  user: PropTypes.object,
  categorizeResults: PropTypes.bool,
  placeholder: PropTypes.string,
  clearInputOnSearch: PropTypes.bool,
  includedSuggestionTypes: PropTypes.array,
  variant: PropTypes.oneOf(['new-mobile-header']),
  recognizedLocations: PropTypes.array,
}

export default Search
export { MAX_AMOUNT_OF_ADDRESS_RESULTS }
